import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ReviewFieldSchemaType, ReviewRating, ReviewStatus } from "./review";

const REVIEW_FIELD_DISCRIMINATOR_KEY = "fieldType";

const _ReviewFieldDbParser = z.object({
  semanticTagId: z.string(),
  fieldType: createEnum(ReviewFieldSchemaType),
});

const ReviewShortAnswerReviewFieldDbParser = _ReviewFieldDbParser.extend({
  fieldType: z.literal(ReviewFieldSchemaType.REVIEW_SHORT_ANSWER),
  text: z.string(),
});

const ReviewParagraphReviewFieldDbParser = _ReviewFieldDbParser.extend({
  fieldType: z.literal(ReviewFieldSchemaType.REVIEW_PARAGRAPH),
  text: z.string(),
});

const ReviewSingleChoiceReviewFieldDbParser = _ReviewFieldDbParser.extend({
  fieldType: z.literal(ReviewFieldSchemaType.REVIEW_SINGLE_CHOICE),
  text: z.string(),
});

const ReviewMediaUploadReviewFieldDbParser = _ReviewFieldDbParser.extend({
  fieldType: z.literal(ReviewFieldSchemaType.REVIEW_MEDIA_UPLOAD),
  text: z.string(),
});

const ReviewRatingReviewFieldDbParser = _ReviewFieldDbParser.extend({
  fieldType: z.literal(ReviewFieldSchemaType.REVIEW_RATING),
  rating: createEnum(ReviewRating),
});

export const ReviewFieldDbParser = z.discriminatedUnion(
  REVIEW_FIELD_DISCRIMINATOR_KEY,
  [
    ReviewShortAnswerReviewFieldDbParser,
    ReviewParagraphReviewFieldDbParser,
    ReviewSingleChoiceReviewFieldDbParser,
    ReviewMediaUploadReviewFieldDbParser,
    ReviewRatingReviewFieldDbParser,
  ],
);

export type ReviewField = z.infer<typeof ReviewFieldDbParser>;

export const ReviewReplyDbParser = z.object({
  text: z.string(),
  createdBy: z.string().optional(),
  createdAt: z.date(),
  isFromMerchant: z.boolean(),
});

export type ReviewReply = z.infer<typeof ReviewReplyDbParser>;

export const ReviewDbParser = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  status: createEnum(ReviewStatus),
  verifiedPurchase: z.boolean(),
  shopifyProductId: z.string(),
  customer: z
    .object({
      email: z.string(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      redoCustomerId: zExt.objectId().nullable().optional(),
    })
    .optional(),
  reviewFields: z.array(ReviewFieldDbParser),
  replies: z.array(ReviewReplyDbParser).optional(),
  rating: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  isEdited: z.boolean().optional(),
  orderId: zExt.objectId().nullable().optional(),
  submittedFromEmailId: zExt.objectId().nullable().optional(),
  source: z.string().optional(),
  externalId: z.string().optional(),
});

export type IReview = z.infer<typeof ReviewDbParser>;

export const ReviewDbParserArray = z.array(ReviewDbParser);
